import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import type { CommonCodeGroupResponse } from '../types/code';

interface CodeGroupDeleteConfirmProps {
  open: boolean;
  group: CommonCodeGroupResponse | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  errorMessage?: string;
}

export function CodeGroupDeleteConfirm({
  open,
  group,
  onClose,
  onConfirm,
  isLoading = false,
  errorMessage,
}: CodeGroupDeleteConfirmProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const isSystemManaged = group?.systemManaged === true;

  return (
    <FormDialog
      open={open}
      title={t('commonCode.deleteGroupConfirm.title')}
      onClose={onClose}
      onSubmit={isSystemManaged ? onClose : onConfirm}
      submitLabel={isSystemManaged ? t('commonCode.deleteGroupConfirm.confirmOk') : isLoading ? t('commonCode.deleteGroupConfirm.deleting') : t('commonCode.deleteGroupConfirm.confirm')}
      cancelLabel={isSystemManaged ? undefined : t('commonCode.deleteGroupConfirm.cancel')}
    >
      <div>
        {/* 시스템 관리 그룹 차단 */}
        {isSystemManaged && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              padding: 'var(--spacing-4)',
              background: 'color-mix(in srgb, var(--color-warning) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-warning)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-warning)',
                fontWeight: 600,
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('commonCode.deleteGroupConfirm.systemManagedTitle')}
            </p>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              <code
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  background: 'var(--color-surface-elevated)',
                  padding: '1px var(--spacing-1)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                systemManaged=true
              </code>{' '}
              {t('commonCode.deleteGroupConfirm.systemManagedDetail')}
            </p>
          </div>
        )}

        {/* 에러 배너 */}
        {errorMessage && !isSystemManaged && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* 삭제 확인 메시지 */}
        {!isSystemManaged && group && (
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
              lineHeight: 1.6,
            }}
          >
            <strong
              style={{
                fontFamily: 'var(--font-family-mono)',
                color: 'var(--color-danger)',
              }}
            >
              {group.groupCode}
            </strong>{' '}
            ({group.groupName}) 그룹을 삭제하시겠습니까?
          </p>
        )}

        {!isSystemManaged && group && (
          <p
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              marginTop: 'var(--spacing-2)',
            }}
          >
            {t('commonCode.deleteGroupConfirm.irreversible')}
          </p>
        )}
      </div>
    </FormDialog>
  );
}
